import { useEffect, useState } from "react";
import { Plus, Search, X, Phone, Calendar, FileText } from "lucide-react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

const supabase = getSupabaseClient();

type Patient = {
  id: string;
  clinic_id?: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  dob: string | null;
  insurance_member_id: string | null;
  insurance_provider: string | null;
  patient_description: string | null;
  intake_completed: boolean | null;
  intake_data: Record<string, unknown> | null;
  last_appointment_date: string | null;
  next_appointment_date: string | null;
  total_appointments: number | null;
  is_recurring: boolean | null;
  notes?: string | null; // TODO: add notes column to patients table in Supabase
  created_at: string;
  updated_at: string;
};

type PatientCallRow = {
  id: string;
  caller_name: string | null;
  phone_number: string | null;
  appointment_booked: boolean | null;
  duration_seconds: number | null;
  summary: string | null;
  created_at: string;
};

type PatientApptRow = {
  id: string;
  patient_name: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  reason_for_visit: string | null;
  appointment_type: string | null;
  provider: string | null;
  status: string | null;
  duration: string | null;
};

const formatPhone = (p: string | null) => {
  if (!p) return "—";
  const d = p.replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1")
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
};

const maskInsuranceId = (id: string | null) => {
  if (!id) return "—";
  if (id.length <= 4) return id;
  return `••••${id.slice(-4)}`;
};

const timeAgo = (dateStr: string | null) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const fmtDuration = (s: number | null) => {
  if (!s) return "—";
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const AVATAR_PALETTE = ["#16A34A", "#2563EB", "#7C3AED", "#D97706", "#DC2626"];
const AVATAR_BG: Record<string, string> = {
  "#16A34A": "#DCFCE7",
  "#2563EB": "#DBEAFE",
  "#7C3AED": "#EDE9FE",
  "#D97706": "#FEF3C7",
  "#DC2626": "#FEE2E2",
};

const Avatar = ({ name, size = 32 }: { name: string; size?: number }) => {
  const letter = (name || "?")[0].toUpperCase();
  const color = AVATAR_PALETTE[letter.charCodeAt(0) % AVATAR_PALETTE.length];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: AVATAR_BG[color] ?? "#F3F4F6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: size * 0.38,
        fontWeight: 700,
        color,
        letterSpacing: "-0.01em",
      }}
    >
      {letter}
    </div>
  );
};

const STATUS_CFG: Record<string, { dot: string; text: string; bg: string }> = {
  scheduled: { dot: "#2563EB", text: "#1D4ED8", bg: "#DBEAFE" },
  completed: { dot: "#16A34A", text: "#15803D", bg: "#DCFCE7" },
  "no-show": { dot: "#DC2626", text: "#B91C1C", bg: "#FEE2E2" },
  no_show: { dot: "#DC2626", text: "#B91C1C", bg: "#FEE2E2" },
  cancelled: { dot: "#9CA3AF", text: "#6B7280", bg: "#F3F4F6" },
  rescheduled: { dot: "#D97706", text: "#92400E", bg: "#FEF3C7" },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_CFG[status.toLowerCase()] ?? STATUS_CFG.scheduled;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 100,
        background: cfg.bg,
        color: cfg.text,
        letterSpacing: "0.02em",
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {status.replace("_", "-")}
    </span>
  );
};

// ── Add Patient Sheet ────────────────────────────────────────────────────────

interface AddPatientForm {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  dob: string;
  insurance_provider: string;
  insurance_member_id: string;
}

const AddPatientSheet = ({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) => {
  const [form, setForm] = useState<AddPatientForm>({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    dob: "",
    insurance_provider: "",
    insurance_member_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setError("First name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from("patients").insert({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      dob: form.dob || null,
      insurance_provider: form.insurance_provider.trim() || null,
      insurance_member_id: form.insurance_member_id.trim() || null,
      intake_completed: false,
      total_appointments: 0,
      is_recurring: false,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      dob: "",
      insurance_provider: "",
      insurance_member_id: "",
    });
    onAdded();
    onClose();
  };

  if (!open) return null;

  const fields = [
    { label: "First name *", key: "first_name", type: "text" },
    { label: "Last name", key: "last_name", type: "text" },
    { label: "Phone", key: "phone", type: "tel" },
    { label: "Email", key: "email", type: "email" },
    { label: "Date of birth", key: "dob", type: "date" },
    { label: "Insurance provider", key: "insurance_provider", type: "text" },
    { label: "Insurance member ID", key: "insurance_member_id", type: "text" },
  ] as const;

  const inputStyle = {
    width: "100%",
    fontSize: 13,
    padding: "8px 12px",
    background: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: 7,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box" as const,
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex" }}>
      <div
        style={{ flex: 1, background: "rgba(0,0,0,0.2)" }}
        onClick={onClose}
      />
      <form
        onSubmit={handleSubmit}
        style={{
          width: 384,
          height: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid #E8E8E8",
          }}
        >
          <p style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
            Add patient
          </p>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9CA3AF",
              padding: 4,
              borderRadius: 6,
              display: "flex",
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div
          style={{
            flex: 1,
            padding: "18px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {error && (
            <p
              style={{
                fontSize: 12,
                padding: "8px 12px",
                borderRadius: 7,
                background: "#FEE2E2",
                color: "#B91C1C",
              }}
            >
              {error}
            </p>
          )}
          {fields.map(({ label, key, type }) => (
            <div key={key}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#6B7280",
                  marginBottom: 5,
                }}
              >
                {label}
              </label>
              <input
                type={type}
                value={form[key as keyof AddPatientForm]}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [key]: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid #E8E8E8",
            display: "flex",
            gap: 8,
          }}
        >
          <button
            type="submit"
            disabled={saving}
            style={{
              flex: 1,
              padding: "9px 0",
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 600,
              background: saving ? "#555555" : "#16A34A",
              color: "#FFFFFF",
              border: "none",
              cursor: saving ? "default" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Add patient"}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "9px 14px",
              borderRadius: 7,
              fontSize: 13,
              background: "#F9FAFB",
              color: "#374151",
              border: "1px solid #E8E8E8",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Detail Panel ─────────────────────────────────────────────────────────────

const PatientDetailPanel = ({
  patient,
  onClose,
}: {
  patient: Patient;
  onClose: () => void;
}) => {
  const [tab, setTab] = useState<
    "overview" | "calls" | "appointments" | "notes"
  >("overview");
  const [calls, setCalls] = useState<PatientCallRow[]>([]);
  const [appts, setAppts] = useState<PatientApptRow[]>([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [note, setNote] = useState(patient.notes || "");
  const [saving, setSaving] = useState(false);

  const fullName =
    [patient.first_name, patient.last_name].filter(Boolean).join(" ") ||
    "Unknown";

  useEffect(() => {
    const phone = patient.phone?.replace(/\D/g, "").slice(-10);
    if (!phone) return;
    setLoadingCalls(true);
    supabase
      .from("conversations")
      .select(
        "id,caller_name,phone_number,appointment_booked,duration_seconds,summary,created_at",
      )
      .ilike("phone_number", `%${phone}%`)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => {
        setCalls(data || []);
        setLoadingCalls(false);
      });
    setLoadingAppts(true);
    supabase
      .from("appointments")
      .select(
        "id,patient_name,appointment_date,appointment_time,reason_for_visit,appointment_type,provider,status,duration",
      )
      .ilike("phone_number", `%${phone}%`)
      .order("appointment_date", { ascending: false })
      .limit(20)
      .then(({ data }) => {
        setAppts(data || []);
        setLoadingAppts(false);
      });
  }, [patient.id, patient.phone]);

  const saveNote = async () => {
    setSaving(true);
    try {
      // TODO: add notes column to patients table in Supabase
      await supabase
        .from("patients")
        .update({ notes: note } as Record<string, unknown>)
        .eq("id", patient.id);
    } catch {
      /* silently ignore if column doesn't exist yet */
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    {
      id: "overview" as const,
      label: "Overview",
      icon: <FileText style={{ width: 12, height: 12 }} />,
    },
    {
      id: "calls" as const,
      label: "Calls",
      icon: <Phone style={{ width: 12, height: 12 }} />,
    },
    {
      id: "appointments" as const,
      label: "Appointments",
      icon: <Calendar style={{ width: 12, height: 12 }} />,
    },
    {
      id: "notes" as const,
      label: "Notes",
      icon: <FileText style={{ width: 12, height: 12 }} />,
    },
  ];

  const overviewFields = [
    { label: "Date of birth", value: patient.dob || "—" },
    { label: "Email", value: patient.email || "—" },
    { label: "Insurance", value: patient.insurance_provider || "—" },
    {
      label: "Insurance ID",
      value: maskInsuranceId(patient.insurance_member_id),
    },
    { label: "Recurring", value: patient.is_recurring ? "Yes" : "No" },
    {
      label: "Intake",
      value: patient.intake_completed ? "Completed" : "Incomplete",
    },
    {
      label: "Added",
      value: new Date(patient.created_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    },
    { label: "Last visit", value: timeAgo(patient.last_appointment_date) },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 22px 14px",
          borderBottom: "1px solid #E8E8E8",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 13,
            marginBottom: 10,
          }}
        >
          <Avatar name={fullName} size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                letterSpacing: "-0.02em",
                marginBottom: 2,
              }}
            >
              {fullName}
            </p>
            <p
              style={{
                fontSize: 12,
                color: "#9CA3AF",
                fontFamily: "monospace",
              }}
            >
              {formatPhone(patient.phone)}
            </p>
          </div>
          {patient.insurance_provider && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "3px 8px",
                borderRadius: 6,
                background: "#F3F4F6",
                color: "#6B7280",
              }}
            >
              {patient.insurance_provider}
            </span>
          )}
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9CA3AF",
              padding: 4,
              borderRadius: 6,
              display: "flex",
              flexShrink: 0,
            }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {patient.patient_description && (
          <p
            style={{
              fontSize: 12,
              color: "#6B7280",
              lineHeight: 1.6,
              background: "#F9FAFB",
              borderRadius: 7,
              padding: "8px 10px",
              border: "1px solid #E8E8E8",
            }}
          >
            {patient.patient_description}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #E8E8E8",
          padding: "0 18px",
          flexShrink: 0,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? "#111827" : "#6B7280",
              background: "transparent",
              border: "none",
              borderBottom:
                tab === t.id ? "2px solid #111111" : "2px solid transparent",
              padding: "8px 10px",
              cursor: "pointer",
              marginBottom: -1,
            }}
          >
            <span style={{ color: tab === t.id ? "#16A34A" : "#D1D5DB" }}>
              {t.icon}
            </span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
        {tab === "overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E8E8E8",
                borderRadius: 9,
                overflow: "hidden",
              }}
            >
              {overviewFields.map((f, i, arr) => (
                <div
                  key={f.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "9px 13px",
                    borderBottom:
                      i < arr.length - 1 ? "1px solid #E8E8E8" : "none",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#9CA3AF",
                    }}
                  >
                    {f.label}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: "#374151",
                      fontFamily:
                        f.label === "Insurance ID" ? "monospace" : "inherit",
                    }}
                  >
                    {f.value}
                  </span>
                </div>
              ))}
            </div>
            {patient.intake_data &&
              Object.keys(patient.intake_data).length > 0 && (
                <div>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "#9CA3AF",
                      marginBottom: 7,
                    }}
                  >
                    Intake notes
                  </p>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.65,
                      background: "#F9FAFB",
                      border: "1px solid #E8E8E8",
                      borderRadius: 9,
                      padding: "12px 14px",
                    }}
                  >
                    {Object.entries(patient.intake_data).map(([k, v]) => (
                      <div key={k} style={{ marginBottom: 4 }}>
                        <span style={{ color: "#9CA3AF" }}>{k}: </span>
                        <span>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}

        {tab === "calls" && (
          <div>
            {loadingCalls ? (
              <p
                style={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  textAlign: "center",
                  paddingTop: 32,
                }}
              >
                Loading…
              </p>
            ) : calls.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  paddingTop: 48,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#F3F4F6",
                    border: "1px solid #E8E8E8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Phone style={{ width: 16, height: 16, color: "#D1D5DB" }} />
                </div>
                <p style={{ fontSize: 13, fontWeight: 500, color: "#6B7280" }}>
                  No calls on record
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {calls.map((c, i) => (
                  <div
                    key={c.id}
                    style={{
                      padding: "11px 0",
                      borderBottom:
                        i < calls.length - 1 ? "1px solid #E8E8E8" : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 100,
                          background: c.appointment_booked
                            ? "#DCFCE7"
                            : "#F3F4F6",
                          color: c.appointment_booked ? "#15803D" : "#6B7280",
                        }}
                      >
                        {c.appointment_booked ? "Booked" : "Info"}
                      </span>
                      <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                        {timeAgo(c.created_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: c.summary ? 3 : 0,
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#6B7280" }}>
                        {fmtDuration(c.duration_seconds)}
                      </span>
                    </div>
                    {c.summary && (
                      <p
                        style={{
                          fontSize: 12,
                          color: "#6B7280",
                          lineHeight: 1.55,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient:
                            "vertical" as React.CSSProperties["WebkitBoxOrient"],
                        }}
                      >
                        {c.summary}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "appointments" && (
          <div>
            {loadingAppts ? (
              <p
                style={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  textAlign: "center",
                  paddingTop: 32,
                }}
              >
                Loading…
              </p>
            ) : appts.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  paddingTop: 48,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#F3F4F6",
                    border: "1px solid #E8E8E8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Calendar
                    style={{ width: 16, height: 16, color: "#D1D5DB" }}
                  />
                </div>
                <p style={{ fontSize: 13, fontWeight: 500, color: "#6B7280" }}>
                  No appointments scheduled
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {appts.map((a, i) => (
                  <div
                    key={a.id}
                    style={{
                      padding: "11px 0",
                      borderBottom:
                        i < appts.length - 1 ? "1px solid #E8E8E8" : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 3,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        {a.appointment_date
                          ? new Date(a.appointment_date).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric" },
                            )
                          : "—"}
                        {a.appointment_time ? ` · ${a.appointment_time}` : ""}
                      </p>
                      {a.status && <StatusBadge status={a.status} />}
                    </div>
                    <p style={{ fontSize: 12, color: "#6B7280" }}>
                      {a.reason_for_visit || a.appointment_type || "General"}
                      {a.provider ? ` · ${a.provider}` : ""}
                      {a.duration ? ` · ${a.duration}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "notes" && (
          <div>
            <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>
              Internal notes visible only to clinic staff.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add notes about this patient…"
              rows={6}
              style={{
                width: "100%",
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                padding: "10px 12px",
                background: "#FFFFFF",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                color: "#111827",
                lineHeight: 1.65,
                boxSizing: "border-box",
                fontFamily: "inherit",
                minHeight: 120,
              }}
              onFocus={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#16A34A";
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#D1D5DB";
              }}
            />
            <button
              onClick={saveNote}
              disabled={saving}
              style={{
                marginTop: 10,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 18px",
                borderRadius: 7,
                background: saving ? "#555555" : "#16A34A",
                color: "#FFFFFF",
                border: "none",
                cursor: saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save note"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Patient List Row ─────────────────────────────────────────────────────────

const PatientRow = ({
  patient,
  selected,
  onClick,
}: {
  patient: Patient;
  selected: boolean;
  onClick: () => void;
}) => {
  const fullName =
    [patient.first_name, patient.last_name].filter(Boolean).join(" ") ||
    "Unknown";
  const callCount = patient.total_appointments ?? 0;

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "11px 14px",
        background: selected ? "#F0FDF4" : "#FFFFFF",
        border: "none",
        borderLeft: selected ? "2px solid #111111" : "2px solid transparent",
        borderBottom: "1px solid #E8E8E8",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#F9FAFB";
      }}
      onMouseLeave={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
      }}
    >
      <Avatar name={fullName} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 2,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#111827",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fullName}
          </p>
          {callCount > 0 && (
            <span
              style={{
                fontSize: 10,
                color: "#9CA3AF",
                flexShrink: 0,
                marginLeft: 6,
              }}
            >
              {callCount}
            </span>
          )}
        </div>
        <p
          style={{
            fontSize: 11,
            color: "#9CA3AF",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {formatPhone(patient.phone)}
          {patient.insurance_provider ? ` · ${patient.insurance_provider}` : ""}
        </p>
      </div>
    </button>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

export const PatientsPage = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterInsurance, setFilterInsurance] = useState<string>("");
  const [filterIntakeCompleted, setFilterIntakeCompleted] =
    useState<string>("all");

  const fetchPatients = async () => {
    setLoading(true);
    try {
      let query = supabase.from("patients").select("*");
      if (filterInsurance)
        query = query.eq("insurance_provider", filterInsurance);
      if (filterIntakeCompleted === "completed")
        query = query.eq("intake_completed", true);
      else if (filterIntakeCompleted === "incomplete")
        query = query.eq("intake_completed", false);
      const { data, error } = await query.order("created_at", {
        ascending: false,
      });
      setPatients(error ? [] : data || []);
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [filterInsurance, filterIntakeCompleted]);

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
    return (
      !q ||
      name.includes(q) ||
      (p.phone || "").includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.insurance_member_id || "").includes(q)
    );
  });

  const insuranceProviders = Array.from(
    new Set(patients.map((p) => p.insurance_provider).filter(Boolean)),
  ).sort() as string[];
  const selectedPatient = patients.find((p) => p.id === selectedId) ?? null;

  const selectStyle = {
    fontSize: 11,
    padding: "5px 8px",
    background: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: 6,
    color: "#374151",
    outline: "none",
    cursor: "pointer",
  } as const;

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 56px)",
        background: "#F7F7F7",
        overflow: "hidden",
      }}
    >
      {/* Left: patient list */}
      <div
        style={{
          width: 320,
          flexShrink: 0,
          borderRight: "1px solid #E8E8E8",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: "#FFFFFF",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 14px 12px", flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#111827",
                  letterSpacing: "-0.02em",
                }}
              >
                Patients
              </p>
              <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>
                {filtered.length} record{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>
            <button
              onClick={() => setAddOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 12px",
                borderRadius: 7,
                background: "#111111",
                color: "#FFFFFF",
                border: "none",
                cursor: "pointer",
              }}
            >
              <Plus style={{ width: 12, height: 12 }} />
              Add
            </button>
          </div>

          <div style={{ position: "relative", marginBottom: 8 }}>
            <Search
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 13,
                height: 13,
                color: "#9CA3AF",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patients…"
              style={{
                width: "100%",
                fontSize: 13,
                background: "#F9FAFB",
                border: "1px solid #E8E8E8",
                borderRadius: 7,
                paddingLeft: 30,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                color: "#111827",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {insuranceProviders.length > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              <select
                value={filterInsurance}
                onChange={(e) => setFilterInsurance(e.target.value)}
                style={{ ...selectStyle, flex: 1 }}
              >
                <option value="">All insurance</option>
                {insuranceProviders.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <select
                value={filterIntakeCompleted}
                onChange={(e) => setFilterIntakeCompleted(e.target.value)}
                style={{ ...selectStyle, flex: 1 }}
              >
                <option value="all">All intake</option>
                <option value="completed">Complete</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div
              style={{
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 54,
                    borderRadius: 7,
                    background: "#F3F4F6",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 48,
                gap: 6,
              }}
            >
              <p style={{ fontSize: 13, fontWeight: 500, color: "#6B7280" }}>
                {search ? "No matches" : "No patients yet"}
              </p>
            </div>
          ) : (
            filtered.map((p) => (
              <PatientRow
                key={p.id}
                patient={p}
                selected={selectedId === p.id}
                onClick={() => setSelectedId(selectedId === p.id ? null : p.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right: detail panel */}
      <div style={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
        {selectedPatient ? (
          <PatientDetailPanel
            key={selectedPatient.id}
            patient={selectedPatient}
            onClose={() => setSelectedId(null)}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 10,
              background: "#F7F7F7",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#FFFFFF",
                border: "1px solid #E8E8E8",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Search style={{ width: 20, height: 20, color: "#D1D5DB" }} />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
              Select a patient
            </p>
            <p style={{ fontSize: 12, color: "#9CA3AF" }}>
              Click any row to view their record
            </p>
          </div>
        )}
      </div>

      <AddPatientSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={fetchPatients}
      />
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
};
